/**
 * <linked-product-option>
 *
 * Legt eine Variante eines anderen Produkts (z. B. die Hülle ohne Füllung) in
 * den Warenkorb, ohne die Produktseite zu verlassen.
 *
 * Nach dem Hinzufügen werden dieselben Sections neu gerendert, die auch das
 * reguläre Produktformular aktualisiert – so bleiben Warenkorb-Zähler und
 * Benachrichtigung konsistent, ohne dass die Seite neu lädt.
 */
class LinkedProductOption extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-add-button]');
    this.label = this.querySelector('[data-add-label]');
    this.spinner = this.querySelector('[data-add-spinner]');
    this.status = this.querySelector('[data-add-status]');
    this.confirm = this.querySelector('[data-add-confirm]');
    if (!this.button) return;

    this.defaultLabel = this.label?.textContent.trim() ?? '';
    this.button.addEventListener('click', this.onClick.bind(this));

    // Nach einem Variantenwechsel wird der Block neu gerendert und verliert
    // seinen Zustand. Deshalb aus dem echten Warenkorb ableiten, ob diese
    // Variante schon drin liegt.
    this.syncFromCart();
  }

  syncFromCart() {
    const variantId = Number(this.dataset.variantId);
    if (!variantId) return;

    fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cartState) => {
        const inCart = (cartState.items || []).some((item) => item.id === variantId);
        if (inCart) this.showConfirmed();
      })
      .catch(() => {
        /* Ohne Warenkorbstand bleibt die Schaltfläche im Ausgangszustand */
      });
  }

  get cartElement() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  onClick() {
    const variantId = this.dataset.variantId;
    if (!variantId || this.loading) return;

    this.setLoading(true);
    this.setStatus('');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);

    const cart = this.cartElement;
    if (cart) {
      formData.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement(document.activeElement);
    }

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          // Shopify liefert bei Fehlern ein status-Feld (z. B. ausverkauft,
          // Mengenlimit erreicht).
          this.setStatus(response.description || response.message || '');
          return;
        }

        if (cart) {
          cart.renderContents(response);
        } else {
          // Bei cart_type "page" gibt es weder Drawer noch Notification im
          // DOM. Dann bleibt der Header-Zähler die einzige Rückmeldung, dass
          // etwas passiert ist – also selbst aktualisieren.
          this.updateCartCount();
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'linked-product-option',
          productVariantId: variantId,
          cartData: response,
        });

        this.showConfirmed();
      })
      .catch(() => {
        this.setStatus('Konnte nicht hinzugefügt werden. Bitte erneut versuchen.');
      })
      .finally(() => this.setLoading(false));
  }

  /**
   * Header-Zähler aus dem aktuellen Warenkorbstand nachziehen. Die Bubble
   * existiert im Markup nur, wenn der Warenkorb nicht leer war – beim ersten
   * Artikel muss sie deshalb erzeugt werden.
   */
  updateCartCount() {
    fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cartState) => {
        const cartLink = document.querySelector('#cart-icon-bubble') || document.querySelector('a[href$="/cart"]');
        if (!cartLink) return;

        let bubble = cartLink.querySelector('.cart-count-bubble');
        if (!bubble) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          cartLink.appendChild(bubble);
        }

        const count = cartState.item_count;
        bubble.innerHTML =
          (count < 100 ? `<span aria-hidden="true">${count}</span>` : '') +
          `<span class="visually-hidden">${count}</span>`;
      })
      .catch(() => {
        /* Zähler nicht aktualisierbar – der Artikel liegt trotzdem im Warenkorb */
      });
  }

  setLoading(isLoading) {
    this.loading = isLoading;
    this.button.classList.toggle('is-loading', isLoading);
    this.button.setAttribute('aria-disabled', String(isLoading));
    this.spinner?.classList.toggle('hidden', !isLoading);
    if (this.label) this.label.classList.toggle('hidden', isLoading);
  }

  /**
   * "Bereits im Warenkorb" ist ein Zustand, kein kurzes Feedback: die Meldung
   * bleibt stehen, solange die Variante im Warenkorb liegt. Der Button
   * verschwindet dabei, damit niemand versehentlich doppelt hinzufügt.
   */
  showConfirmed() {
    this.classList.add('is-added');
    this.confirm?.removeAttribute('hidden');
    this.button.setAttribute('hidden', '');
    this.setStatus('');
  }

  setStatus(message) {
    if (this.status) this.status.textContent = message;
  }
}

if (!customElements.get('linked-product-option')) {
  customElements.define('linked-product-option', LinkedProductOption);
}
